import styled from "@emotion/styled";
import { navigate } from "gatsby";
import URL from "core-js-pure/features/url";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import queryString from "query-string";

import Radio from "~/components/FormElements/Radio";
import SearchInput from "~/components/FormElements/SearchInput";

import IcoIcon from "~/components/IcoIcon";

import transition from "~/utilities/transition";

import { screenReaderText, ScreenReaderText } from "~/styles/screenReaderText";

import { useSearchContext } from "~/context/SearchContext";
import { useLocationContext } from "~/context/LocationContext";

const Wrapper = styled.div`
    ${transition({}, "opacity", "visibility")};
    position: absolute;
    top: 0;
    left: 0;
    transform: none;
    width: 80%;
    background-color: ${({ theme }) => theme.palette.whiteOverlay};
    display: ${({ isOpen }) => (isOpen ? `flex` : `none`)};
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    z-index: ${({ theme }) => theme.zIndex.dropdown};
    padding: 15px 15px 0px 50px;
    height: 100vh;
    overflow-y: ${({ isOpen }) => (isOpen ? `scroll` : `hidden`)};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        top: 100%;
        left: 50%;
        width: 100%;
        transform: translate(-50%, 0);
        max-height: 225px;
        align-items: center;
        justify-content: center;
        overflow-y: visible;
    }
`;

const SearchCloseWrapper = styled.div`
    padding: 10px 0 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        display: none;
    }
`;

const SearchClose = styled.button`
    color: ${({ theme }) => theme.palette.darkBlue};
`;

const StyledForm = styled.form`
    max-width: 250px;
    padding: ${({ theme }) => theme.layout.gutter} 0;
    display: flex;
    flex-direction: column;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        max-width: 650px;
    }
`;

const Label = styled.label`
    font-size: 1.9rem;
    font-weight: 900;
    margin-bottom: 15px;
    color: ${({ theme }) => theme.palette.darkBlue};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        ${screenReaderText};
    }
`;

const Error = styled.div`
    margin-bottom: 15px;
    font-weight: 900;
    font-size: 1.5rem;
    color: ${({ theme }) => theme.palette.red};
`;

const HeaderSearch = ({
    isOpen = false,
    onClick = () => {},
    headerSearchOptions = [],
    searchWrapperRef,
}) => {
    const [searchValues, setSearchValues] = useState({
        searchText: "",
        searchType: headerSearchOptions[0].value,
    });

    const { location } = useLocationContext();

    const [error, setError] = useState("");

    const { setKeyword } = useSearchContext();

    useEffect(() => {
        if (!isOpen && searchValues.searchText) {
            setSearchValues({
                ...searchValues,
                searchText: "",
            });
        }
    }, [isOpen, searchValues]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setSearchValues({
            ...searchValues,
            [name]: value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!searchValues.searchText) {
            setError("Please enter a search term");
            return;
        }

        const params = queryString.parse(location.search);

        const option = headerSearchOptions.find(
            ({ value }) => value === searchValues.searchType,
        );

        const url = new URL(`${process.env.GATSBY_SITE_URL}${option.pageUrl}`);

        for (const key in params) {
            url.searchParams.set(key, params[key]);
        }

        url.searchParams.set("keyword", searchValues.searchText);

        setKeyword(searchValues.searchText);
        navigate(`${url.pathname}${url.search}`);
    };

    return (
        <Wrapper {...{ isOpen }} ref={searchWrapperRef}>
            <SearchCloseWrapper>
                <SearchClose {...{ onClick }}>
                    <ScreenReaderText>Close Search</ScreenReaderText>
                    <IcoIcon icon={"close"} />
                </SearchClose>
            </SearchCloseWrapper>
            <StyledForm onSubmit={handleSubmit}>
                <Label htmlFor="search-input">Search Input</Label>
                <SearchInput
                    value={searchValues.searchText}
                    onChange={handleChange}
                    focus={isOpen}
                    id="search-input"
                    type="text"
                    name="searchText"
                    buttonScreenReaderText="Click to search"
                    icon="search"
                />

                {error ? <Error>{error}</Error> : null}

                {headerSearchOptions?.length ? (
                    <Radio
                        {...{ handleChange }}
                        label="Select search category"
                        labelPlacement="hidden_label"
                        name="searchType"
                        options={headerSearchOptions}
                        value={searchValues.searchType}
                    />
                ) : null}
            </StyledForm>
        </Wrapper>
    );
};

HeaderSearch.propTypes = {
    isOpen: PropTypes.bool,
    onClick: PropTypes.func,
    headerSearchOptions: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string,
            value: PropTypes.string,
        }),
    ),
};

export default HeaderSearch;
